import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import { valueTitleOf } from './app-select-title'

export interface AppSelectOption<TValue> {
  label: string
  value: TValue
}

export interface AppSelectGroup<TValue> {
  label: string
  // Détail affiché à droite de l'intitulé de section (ex. nombre d'options).
  meta?: string
  options: readonly AppSelectOption<TValue>[]
}

export interface AppSelectProps<TValue> {
  id?: string
  options?: readonly AppSelectOption<TValue>[]
  // Options regroupées en sections (chaque section a un intitulé). Prioritaire sur `options` :
  // quand `groups` est fourni, le menu affiche des en-têtes de section (ex. « En préparation »).
  groups?: readonly AppSelectGroup<TValue>[]
  className?: string
  // Classes du menu déroulant (par défaut aussi large que le déclencheur), ex.
  // `w-auto min-w-(--anchor-width)` pour qu'il s'élargisse à ses libellés.
  contentClassName?: string
  // Classes des lignes et des intitulés de section du menu.
  itemClassName?: string
  groupLabelClassName?: string
  onChange?: (value: TValue | null) => void
  // `null` = contrôlé sans valeur (option « aucune valeur ») ; `undefined` = non contrôlé. Un
  // sélecteur piloté par l'URL doit passer `?? null` : base-ui fige le mode au premier rendu, et un
  // `undefined` le laisserait garder l'ancien choix quand l'URL est remise à zéro ailleurs.
  value?: TValue | null
  defaultValue?: TValue
  ariaInvalid?: boolean
  placeholder?: string
  // Libellé de l'option « aucune valeur » dans le menu (ex. « Tous les départements ») ; le
  // déclencheur affiche toujours `placeholder` (ex. « Départements ») quand rien n'est choisi.
  placeholderOptionLabel?: string
  // `false` : pas d'option « aucune valeur » dans le menu (valeur requise) ; le déclencheur affiche
  // toujours `placeholder` tant que rien n'est choisi.
  placeholderInMenu?: boolean
  size?: 'sm' | 'default' | 'xs' | '2xs'
  disabled?: boolean
  // Infobulle native du déclencheur avec le libellé choisi, pour un déclencheur à largeur bornée
  // dont le texte peut être tronqué.
  valueTitle?: boolean
}

export function AppSelect<TValue>({
  id,
  options = [],
  groups,
  onChange,
  className,
  contentClassName,
  itemClassName,
  groupLabelClassName,
  value,
  defaultValue,
  ariaInvalid,
  placeholder,
  placeholderOptionLabel,
  placeholderInMenu = true,
  size = 'default',
  disabled = false,
  valueTitle = false,
}: AppSelectProps<TValue>) {
  // `items` alimente le typeahead et la résolution valeur→label de base-ui (texte du déclencheur) :
  // il doit lister TOUTES les options sélectionnables, sections confondues. L'option « aucune
  // valeur » y porte `placeholder`, son libellé de menu peut différer (`placeholderOptionLabel`).
  const flatOptions = groups ? groups.flatMap((group) => group.options) : options
  const placeholderOption = placeholder ? [{ label: placeholder, value: null }] : []
  const items = [...placeholderOption, ...flatOptions]

  return (
    <Select
      id={id}
      items={items}
      value={value}
      defaultValue={defaultValue}
      disabled={disabled}
      onValueChange={(value) => {
        onChange?.(value)
      }}
    >
      <SelectTrigger
        className={className}
        aria-invalid={ariaInvalid}
        size={size}
        disabled={disabled}
      >
        {/* Infobulle sur tout le libellé, lue dans la valeur que tient base-ui (contrôlé ou non) ;
            le texte reste résolu par base-ui. */}
        <SelectValue
          render={
            valueTitle
              ? (props, state) => (
                  <span
                    {...props}
                    title={valueTitleOf(flatOptions, state.value as TValue | null)}
                  />
                )
              : undefined
          }
        />
      </SelectTrigger>

      <SelectContent alignItemWithTrigger={false} className={contentClassName}>
        {placeholderInMenu &&
          placeholderOption.map((option) => (
            <SelectItem key={option.label} value={option.value} className={itemClassName}>
              {placeholderOptionLabel ?? option.label}
            </SelectItem>
          ))}
        {groups
          ? groups.map((group) => (
              <SelectGroup key={group.label}>
                <SelectLabel className={groupLabelClassName}>
                  {group.meta === undefined ? (
                    group.label
                  ) : (
                    // L'espace sépare les deux dans le nom accessible (« Ateliers 3 ») ; la mise en
                    // page flex l'ignore.
                    <>
                      <span>{group.label}</span>{' '}
                      <span data-slot="select-label-meta">{group.meta}</span>
                    </>
                  )}
                </SelectLabel>
                {group.options.map((option) => (
                  <SelectItem key={option.label} value={option.value} className={itemClassName}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))
          : options.map((option) => (
              <SelectItem key={option.label} value={option.value} className={itemClassName}>
                {option.label}
              </SelectItem>
            ))}
      </SelectContent>
    </Select>
  )
}
